import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Divider, Stack, Tooltip, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import Table, { TColumn } from '~/components/common/Table/Table';
import themes, { styled } from '~/themes';
import {
  Task as ITask,
  LocationInfo as ITaskLocation,
  TaskStatus,
} from '~/pages/LiveTracking/types';
import { convertedSortParams, downloadCSV } from '~/utils/common';
import {
  formatTimeUTC,
  formatTaskDateTime,
  convertShortString,
  formatETADateTime,
} from '~/utils/formatter';
import InfoIcon from '~/assets/images/icons/info-gray.svg';
import Tag from '~/components/common/Tag';
import {
  addToQueryString,
  fromQueryString,
  omitFromQueryString,
} from '~/utils/queryString';
import { Rating } from '~/components/common/Rating';
import { ITabValue } from '~/models/shared';
import { usePaymentInfo } from '~/providers/PaymentProvider';
import BackgroundImage from '~/assets/images/background/setup-task.png';
import clsx from 'clsx';
import moment from 'moment';
import CheckBox from '~/components/common/CheckBox';
import { find, get, uniqBy } from 'lodash';
import CloseIcon from '~/assets/images/icons/close-white.svg';
import CheckboxIcon from '~/assets/images/icons/checkbox.svg';
import Button from '~/components/common/Button';
import { useExportTasks, useBulkUpdateTaskStatus } from '~/services/api/task';
import { StyledPanelDetailContainer } from '~/themes/style';
import { IStore } from '~/models/stores';
import { useAuth } from '~/providers/AuthProvider';
import { showAlert, alertParams } from '~/components/common/Alert';
import ExportIcon from '~/assets/images/icons/export.svg';
import DriverIcon from '~/assets/images/icons/available-driver.svg';
import CompleteIcon from '~/assets/images/icons/double-check.svg';
import { queryClient } from '~/lib/react-query';
import { AssignDriver } from '../LiveTracking/components/TaskDetail/components/AssignDriver';
import { LiveTrackingQueryKeys } from '../LiveTracking/apis';

const EmptyStateData = (tabValue: ITabValue) => [
  {
    name: 'C-1010',
    status: tabValue === ITabValue.Completed ? 'completed' : 'delivering',
    expected_delivery_after: moment().set({ hour: 13, minute: 0, second: 0 }),
    expected_delivery_before: moment().set({ hour: 14, minute: 0, second: 0 }),
    executor: {
      display_name: 'Jane Doe',
    },
    pickup: {
      address_1: '1010 Kingsway',
      city: 'Burnaby',
      state: 'BC',
      postcode: 'V0H 0B0',
    },
    delivery: {
      address_1: '1010 W Georgia St',
      city: 'Vancouver',
      state: 'BC',
      postcode: 'V0Y 0G0',
    },
    rating: 3,
    updated_at: moment()
      .subtract({ day: 2 })
      .set({ hour: 10, minute: 10, second: 0 }),
  },
];

export const StyledTaskId = styled(Stack)(({ theme }) => ({
  'flexDirection': 'row',
  'alignItems': 'center',
  '& > span': {
    marginRight: theme.spacing(0.5),
  },
  '& .MuiCheckbox-root': {
    width: '36px',
    height: '36px',
  },
}));

export const StyledAllTaskCompletedContainer = styled('div')(({ theme }) => ({
  'position': 'absolute',
  'display': 'flex',
  'flexDirection': 'column',
  'alignItems': 'center',
  'justifyContent': 'center',
  'padding': theme.spacing(2),
  'height': `${theme.spacing(36)}`,
  'width': `calc(100% - ${theme.spacing(5)})`,
  'zIndex': 2,
  '& > h1': {
    marginTop: theme.spacing(3),
    fontSize: 14,
    paddingTop: 0,
    fontWeight: 400,
  },
  [theme.breakpoints.down('md')]: {
    '& > img:first-of-type': {
      maxWidth: '100%',
    },
  },
}));

interface ITaskTableProps {
  data?: ITask[];
  stores?: IStore[];
  isMultiStore?: boolean;
  isFullEmpty?: boolean;
  isLoading?: boolean;
  handleSort: (sortId: string, nextArrange: number) => void;
  isEmptyState?: boolean;
  renderEmpty?: React.ReactNode;
  timezone?: string;
}

export const TaskTable: React.FC<ITaskTableProps> = ({
  data = [],
  stores = [],
  isMultiStore,
  isLoading,
  handleSort,
  isFullEmpty,
  isEmptyState,
  renderEmpty,
  timezone,
}) => {
  const { unifiedNavigation } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isStarterPlan, isBundlePlan } = usePaymentInfo();
  const [tasksSelected, setTasksSelected] = useState<ITask[]>([]);
  const [openBulkAssign, setOpenBulkAssign] = useState(false);
  const lastRowRef = useRef<HTMLDivElement | null>(null);

  const taskIdsSelected = useMemo(
    () => tasksSelected?.map((task) => task.id),
    [tasksSelected],
  );

  const hasPermissionBS = useMemo(() => {
    const retail = find(get(unifiedNavigation, 'section1'), {
      id: 'retail',
    });
    return find(get(retail, 'section2'), { id: 'breadstack_order' });
  }, [unifiedNavigation]);

  const multiTaskIds = useMemo(() => {
    const TASK_STATUS = [
      TaskStatus.Unassigned,
      TaskStatus.PendingPickup,
      TaskStatus.Delivering,
      TaskStatus.Returning,
    ];
    return tasksSelected
      ?.filter((task) => TASK_STATUS?.includes(task.status))
      ?.map((task) => task.id);
  }, [tasksSelected]);

  const handleDownloadCSV = (csvString: string) => {
    downloadCSV(csvString, 'Export_Task_List');
  };

  const { mutate: exportTaskMutation, isLoading: isLoadingExport } =
    useExportTasks({
      onSuccess: (csvData) => {
        handleDownloadCSV(csvData?.csv);
        setTasksSelected([]);
      },
    });

  const {
    mutate: bulkUpdateTaskStatusMutation,
    isLoading: isLoadingBulkUpdate,
  } = useBulkUpdateTaskStatus({
    onSuccess: () => {
      queryClient.invalidateQueries([LiveTrackingQueryKeys.TasksList]);
      setTasksSelected([]);
    },
  });

  const handleBulkCompleteTasks = () => {
    showAlert({
      ...alertParams.warning,
      title: 'Complete tasks',
      cancelText: 'Cancel',
      okText: 'Continue',
      description: `Once the tasks are completed, they'll be removed from\nthe Drive App. Are you sure you want to continue?`,
      onOk: () => {
        bulkUpdateTaskStatusMutation({
          task_ids: multiTaskIds,
          status: 'completed',
          force_complete: true,
        });
      },
    });
  };

  const { id: taskId, sort, sort_by, tab } = fromQueryString(location.search);
  const tabValue = (tab || ITabValue.Ongoing) as ITabValue;
  const dateFormats = {
    sameDay: '[Today]',
    nextDay: '[Tomorrow]',
    lastDay: '[Yesterday]',
    nextWeek: 'MMM D',
    lastWeek: 'MMM D',
  };

  const onRowClick = useCallback(
    (item: { id: string }) => {
      let search;
      if (item.id === taskId) {
        search = omitFromQueryString(location.search, ['id']);
      } else {
        search = addToQueryString(location.search, {
          id: item.id,
        });
      }
      navigate({
        pathname: location.pathname,
        search,
      });
    },
    [location],
  );

  const clickableRowId = useMemo(
    () => taskId && data.findIndex((x) => x.id === taskId),
    [data, taskId],
  );

  const checkAllTask = useMemo(
    () => data.every((x) => taskIdsSelected.includes(x.id)),
    [data, taskIdsSelected],
  );

  const isIndeterminate = useMemo(() => {
    const selectedTasksInPage = data.filter((x) =>
      taskIdsSelected.includes(x.id),
    );
    return (
      selectedTasksInPage.length > 0 && selectedTasksInPage.length < data.length
    );
  }, [data, taskIdsSelected]);

  const renderTaskId = useCallback(
    (item: ITask, _, index: number) => {
      const isLastItem = index === data.length - 1;
      const storeName =
        stores?.find((store) => store?.id === item.created_by)?.display_name ||
        '';
      const tooltipTitle = storeName ? `Storefront: ${storeName}` : '';
      return (
        <StyledTaskId ref={isLastItem ? lastRowRef : null}>
          <CheckBox
            defaultCheckedIcon
            checked={taskIdsSelected?.includes(item.id)}
            disabled={isFullEmpty}
            onChange={(event) => {
              const newVal = event.target.checked;
              if (newVal) {
                setTasksSelected((prev) => [...prev, item]);
                if (isLastItem && taskIdsSelected?.length === 0) {
                  setTimeout(() => {
                    lastRowRef.current?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                    });
                  }, 150);
                }
              } else {
                setTasksSelected((prev) =>
                  prev.filter((task) => task.id !== item.id),
                );
              }
            }}
          />
          <Tooltip
            title={isBundlePlan && isMultiStore && tooltipTitle}
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: themes.color.violet500,
                  borderRadius: '4px',
                  fontSize: 14,
                  fontWeight: 400,
                  padding: '7px 5px',
                },
              },
            }}
          >
            <Typography
              sx={{
                textDecorationLine:
                  item?.integration_type &&
                  isBundlePlan &&
                  (hasPermissionBS || (tooltipTitle && isMultiStore))
                    ? 'underline'
                    : '',
                color:
                  hasPermissionBS && item?.integration_type
                    ? '#2D91FC'
                    : '#33363D',
              }}
              data-testid={`task-id-${item.name}`}
              onClick={(e) => {
                if (hasPermissionBS && isBundlePlan && item?.integration_type) {
                  e.stopPropagation();
                  window.open(item.breadstack_url, '_blank');
                }
              }}
            >
              {item.name}
            </Typography>
          </Tooltip>
          {item?.note && (
            <Tooltip
              title={item?.note}
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: themes.color.violet500,
                    borderRadius: '4px',
                    fontSize: 14,
                    fontWeight: 400,
                    padding: '7px 5px',
                  },
                },
              }}
            >
              <img
                width={16}
                height={16}
                src={InfoIcon}
                alt='info'
                style={{
                  marginLeft: '5px',
                  paddingTop: '2px',
                }}
              />
            </Tooltip>
          )}
        </StyledTaskId>
      );
    },
    [
      taskIdsSelected,
      setTasksSelected,
      stores,
      isMultiStore,
      hasPermissionBS,
      isBundlePlan,
      isFullEmpty,
    ],
  );

  const renderStore = useCallback(
    (item: ITask) => {
      if (item?.store_name) return <Typography>{item?.store_name}</Typography>;
      return <Typography color='rgba(51, 54, 61, 0.40)'>-</Typography>;
    },
    [stores],
  );

  const renderPickup = useCallback(
    (item: ITask) => {
      const { status, skip_pickup } = item;
      if (skip_pickup)
        return <Typography color='rgba(51, 54, 61, 0.40)'>N/A</Typography>;

      if (
        status === TaskStatus.PendingPickup ||
        status === TaskStatus.Unassigned
      )
        return 'ASAP';

      if (status === TaskStatus.Delivering || status === TaskStatus.Returning)
        return item.picked_up_at ? (
          <>
            <div>{formatTimeUTC(item.picked_up_at, 'h:mm a', true)}</div>
            <div>{formatTaskDateTime(item.picked_up_at, dateFormats)}</div>
          </>
        ) : (
          <Typography color='rgba(51, 54, 61, 0.40)'>-</Typography>
        );

      return '';
    },
    [isStarterPlan, isFullEmpty],
  );

  const renderDropoffETA = useCallback(
    (item: ITask) => {
      const {
        status,
        expected_delivery_after,
        expected_delivery_before,
        is_active,
        estimated_delivery_time,
        delivery_arr_time,
      } = item;

      if (
        status === TaskStatus.PendingPickup ||
        status === TaskStatus.Unassigned
      ) {
        if (!expected_delivery_after && !expected_delivery_before)
          return 'ASAP';
        if (!expected_delivery_after && expected_delivery_before)
          return (
            <>
              <div>ASAP</div>
              <div>
                {formatTaskDateTime(expected_delivery_before, dateFormats)}
              </div>
            </>
          );
        return (
          <>
            <div>{formatTimeUTC(expected_delivery_before, 'h:mm a', true)}</div>
            <div>
              {formatTaskDateTime(expected_delivery_before, dateFormats)}
            </div>
          </>
        );
      }

      if (status === TaskStatus.Returning) return 'ASAP';
      if (status === TaskStatus.Delivering) {
        const formatDate = {
          sameDay: 'h:mm a[\n][Today]',
          nextDay: 'h:mm a[\n][Tomorrow]',
          lastDay: 'h:mm a[\n][Yesterday]',
        };
        if (is_active)
          return estimated_delivery_time ? (
            <div style={{ whiteSpace: 'pre' }}>
              {formatETADateTime(
                moment().add(estimated_delivery_time, 'm'),
                formatDate,
                `[\n]`,
              )}
            </div>
          ) : (
            '-'
          );
        return delivery_arr_time ? (
          <div style={{ whiteSpace: 'pre' }}>
            {formatETADateTime(
              moment().add(delivery_arr_time, 's'),
              formatDate,
              `[\n]`,
            )}
          </div>
        ) : (
          '-'
        );
      }

      return '';
    },
    [isStarterPlan, isFullEmpty],
  );

  const renderDriver = useCallback((item: ITask) => {
    if (item.executor) {
      const maxLength = 22;
      const isShowTooltip = item?.executor?.display_name?.length > maxLength;
      return (
        <Tooltip title={isShowTooltip ? item.executor.display_name : ''}>
          <span>
            {convertShortString(item?.executor?.display_name || '', maxLength)}
          </span>
        </Tooltip>
      );
    }
    return <Typography color='rgba(51, 54, 61, 0.40)'>-</Typography>;
  }, []);

  const renderStatus = useCallback(
    (item: ITask) => (
      <Tag
        size='small'
        style={{ width: 'auto' }}
        tag={item.status}
        sx={{
          ...(isFullEmpty
            ? {
                background: 'rgba(0,0,0,0.1) !important',
                color: 'rgba(0,0,0,0.8) !important',
              }
            : {}),
          'fontWeight': 500,
          '& .MuiChip-label': {
            padding: '4px 8px',
          },
        }}
      />
    ),
    [isFullEmpty],
  );

  const renderLocation = useCallback((taskLocation: ITaskLocation) => {
    if (taskLocation) {
      const maxLength = 30;
      const locationLine1 = taskLocation?.address_1;
      const locationLine2 = `${taskLocation.city}, ${taskLocation.state} ${taskLocation.postcode}`;
      const isShowTooltip =
        locationLine1.length > maxLength || locationLine2.length > maxLength;

      return (
        <Tooltip
          title={
            isShowTooltip ? (
              <>
                <div>{locationLine1}</div>
                <div>{locationLine2}</div>
              </>
            ) : (
              ''
            )
          }
        >
          <div>
            <div>{convertShortString(locationLine1, maxLength)}</div>
            <div>{convertShortString(locationLine2, maxLength)}</div>
          </div>
        </Tooltip>
      );
    }
    return <Typography color='rgba(51, 54, 61, 0.40)'>N/A</Typography>;
  }, []);

  const renderRecipient = useCallback((customerName?: string) => {
    const maxLength = 12;
    const isShowTooltip = customerName?.length > maxLength;

    return (
      <Stack
        flexDirection='row'
        sx={{
          width: 120,
        }}
      >
        <Tooltip title={isShowTooltip ? <div>{customerName}</div> : ''}>
          <div>
            {customerName && (
              <Typography
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: '1',
                  WebkitBoxOrient: 'vertical',
                  wordBreak: 'break-all',
                  maxWidth: 100,
                }}
              >
                {customerName}
              </Typography>
            )}
          </div>
        </Tooltip>
      </Stack>
    );
  }, []);

  const renderDeliveryTime = useCallback((item: ITask) => {
    const { status, completed_at, returned_at, delivered_at } = item;

    let time = '';
    if (status === TaskStatus.Returned) {
      time = returned_at;
    }
    if (status === TaskStatus.Completed) {
      time = delivered_at;
    }
    if (status === TaskStatus.Cancelled) {
      time = completed_at;
    }

    return (
      <>
        <div>{formatTimeUTC(time, 'h:mm a', true)}</div>
        <div>{formatTaskDateTime(time, dateFormats)}</div>
      </>
    );
  }, []);

  const renderScheduledDeliveryTime = useCallback(
    (item: ITask) => {
      if (isFullEmpty) {
        return '1 p.m. - 2 p.m. Today';
      }
      if (isStarterPlan) {
        return '-';
      }
      const { expected_delivery_after, expected_delivery_before } = item;
      if (!expected_delivery_after && !expected_delivery_before) {
        return 'ASAP';
      }
      if (!expected_delivery_after && expected_delivery_before) {
        return (
          <>
            <div>ASAP</div>
            <div>
              {formatTaskDateTime(expected_delivery_before, dateFormats)}
            </div>
          </>
        );
      }

      return (
        <>
          <div>{`${formatTimeUTC(
            expected_delivery_after,
            'h:mm a',
            true,
          )} - ${formatTimeUTC(
            expected_delivery_before,
            'h:mm a',
            true,
          )}`}</div>
          <div>{formatTaskDateTime(expected_delivery_before, dateFormats)}</div>
        </>
      );
    },
    [isStarterPlan, isFullEmpty],
  );

  const columns: TColumn[] = useMemo(() => {
    let d = [
      {
        id: 'name',
        title: (
          <StyledTaskId>
            <CheckBox
              defaultCheckedIcon
              checked={checkAllTask}
              onChange={(event) => {
                const newVal = event.target.checked;
                if (newVal) {
                  setTasksSelected((prev) => uniqBy([...prev, ...data], 'id'));
                } else {
                  setTasksSelected((prev) =>
                    prev.filter(
                      (current) =>
                        data.findIndex((x) => x.id === current.id) === -1,
                    ),
                  );
                }
              }}
              indeterminate={isIndeterminate}
            />
            Task/Order
          </StyledTaskId>
        ),
        sortable: true,
        renderComponent: renderTaskId,
        cellStyle: {
          width: 120,
        },
      },
      {
        id: 'status',
        title: 'Status',
        sortable: true,
        cellStyle: { width: 120 },
        renderComponent: renderStatus,
      },
      {
        id: 'store_name',
        title: 'Store',
        sortable: true,
        cellStyle: { maxWidth: 160 },
        renderComponent: renderStore,
      },
      {
        id: 'expected_delivery_before',
        title: 'Scheduled time',
        sortable: !isStarterPlan,
        cellStyle: { maxWidth: 190, minWidth: 145 },
        renderComponent: renderScheduledDeliveryTime,
      },
      {
        id: 'executor_id',
        title: 'Driver',
        sortable: true,
        cellStyle: { maxWidth: 120 },
        renderComponent: renderDriver,
      },
      {
        id: 'pickup_address',
        title: 'Pickup location',
        sortable: true,
        cellStyle: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: 170,
        },
        renderComponent: (item) => renderLocation(item.pickup),
      },
      {
        id: 'picked_up_at',
        title: 'Picked up time',
        sortable: true,
        cellStyle: { maxWidth: 180 },
        renderComponent: renderPickup,
      },
      {
        id: 'recipient',
        title: 'Recipient',
        sortable: true,
        cellStyle: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: 120,
        },
        renderComponent: (item) => renderRecipient(item.delivery?.name),
      },
      {
        id: 'delivery_address',
        title: 'Dropoff location',
        sortable: true,
        cellStyle: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: 170,
        },
        renderComponent: (item) => renderLocation(item.delivery),
      },
      {
        id: 'delivery_arr_time',
        title: 'Dropoff ETA',
        cellStyle: { maxWidth: 180 },
        renderComponent: renderDropoffETA,
      },
      {
        id: 'delivered_at',
        title: 'Completed time',
        sortable: true,
        cellStyle: { maxWidth: 180 },
        renderComponent: renderDeliveryTime,
      },
      {
        id: 'rating',
        title: 'Rating',
        sortable: !isStarterPlan,
        cellStyle: {
          width: 100,
        },
        renderComponent: (item) => {
          if (isStarterPlan && !isFullEmpty) {
            return '-';
          }
          if (item?.rating) {
            return <Rating value={item?.rating} readOnly />;
          }
          return '-';
        },
      },
    ];

    if (isBundlePlan) {
      d = d.filter((c) => !['store_name'].includes(c.id));
    }

    if (tabValue === ITabValue.Ongoing) {
      return d.filter((c) => !['rating', 'delivered_at'].includes(c.id));
    }

    return d.filter(
      (c) =>
        ![
          'picked_up_at',
          'delivery_arr_time',
          'expected_delivery_before',
        ].includes(c.id),
    );
  }, [
    tabValue,
    isStarterPlan,
    isFullEmpty,
    taskIdsSelected,
    checkAllTask,
    isIndeterminate,
    data,
    stores,
    isMultiStore,
    hasPermissionBS,
  ]);

  const isAllTaskOngoingDone = tabValue === ITabValue.Ongoing && isEmptyState;

  const renderTableEmptyState = useMemo(() => {
    if (isFullEmpty) {
      return (
        <StyledAllTaskCompletedContainer>
          {renderEmpty}
        </StyledAllTaskCompletedContainer>
      );
    }
    if (isAllTaskOngoingDone) {
      return (
        <StyledAllTaskCompletedContainer>
          <img width={400} src={BackgroundImage} alt='setup-your-store' />
          <Typography variant='h1'>All tasks are completed!</Typography>
        </StyledAllTaskCompletedContainer>
      );
    }
    return null;
  }, [tabValue, isAllTaskOngoingDone, isFullEmpty, renderEmpty]);

  const renderTaskSelected = () => (
    <Stack
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '2px',
        backgroundColor: themes.color.violet700,
        padding: 1.5,
        zIndex: 10,
        [themes.breakpoints.up('md')]: {
          flexDirection: 'row',
          justifyContent: 'space-between',
        },
        [themes.breakpoints.down('sm')]: {
          padding: 0.8,
        },
      }}
    >
      <Stack
        flexDirection='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{
          width: '100%',
          [themes.breakpoints.up('md')]: {
            width: 'auto',
          },
        }}
      >
        <Stack
          flexDirection='row'
          alignItems='center'
          gap={0.5}
          px={1.6}
          sx={{
            color: 'white',
            fontWeight: 500,
            cursor: 'pointer',
            [themes.breakpoints.down('md')]: {
              px: 0.8,
            },
          }}
          onClick={() => {
            setTasksSelected([]);
            setOpenBulkAssign(false);
          }}
        >
          <img
            src={CloseIcon}
            alt=''
            style={{
              width: 9,
              height: 9,
            }}
          />
          Discard
        </Stack>
        <Typography
          px={1.6}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 0.8,
            color: 'white',
            fontWeight: 500,
            [themes.breakpoints.up('md')]: {
              display: 'none',
            },
            [themes.breakpoints.down('md')]: {
              px: 0.2,
            },
          }}
        >
          <img src={CheckboxIcon} alt='' />
          {taskIdsSelected.length} selected
        </Typography>
      </Stack>
      <Stack
        flexDirection='column'
        gap={0.5}
        mt={1}
        sx={{
          [themes.breakpoints.up('md')]: {
            flexDirection: 'row',
            mt: 0,
            gap: 0.8,
            pl: 1.6,
            alignItems: 'center',
          },
        }}
      >
        <Typography
          px={1.6}
          sx={{
            display: 'none',
            [themes.breakpoints.up('md')]: {
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 0.8,
              color: 'white',
              fontWeight: 500,
              px: 0,
              pr: 1.6,
            },
          }}
        >
          <img src={CheckboxIcon} alt='' />
          {taskIdsSelected.length} selected
        </Typography>
        <Button
          noRounder
          buttonType='text'
          onClick={() =>
            exportTaskMutation({
              ids: taskIdsSelected,
              timezone,
            })
          }
          sx={{
            minWidth: '76px !important',
            backgroundColor: 'rgba(234, 234, 248, 1) !important',
            color: `${themes.color.violet700} !important`,
            padding: '4px 16px',
            borderRadius: '8px !important',
            [themes.breakpoints.down('md')]: {
              minWidth: 'unset !important',
              padding: '4px 8px',
              width: '100%',
            },
          }}
          loading={isLoadingExport}
        >
          <Typography
            variant='body1'
            sx={{
              cursor: 'pointer',
              color: themes.color.violet500,
              fontWeight: 500,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.4,
            }}
          >
            <img src={ExportIcon} alt='export-icon' /> Export List
          </Typography>
        </Button>
        {tabValue === ITabValue.Ongoing && (
          <>
            <Button
              noRounder
              buttonType='text'
              onClick={handleBulkCompleteTasks}
              sx={{
                'minWidth': '100px !important',
                'backgroundColor': 'rgba(234, 234, 248, 1) !important',
                'color': `${themes.color.violet700} !important`,
                'padding': '4px 16px',
                'borderRadius': '8px !important',
                '&.Mui-disabled': {
                  color: ' #C9C9C9 !important',
                  backgroundColor: '#F8F8F8 !important',
                },
                [themes.breakpoints.down('md')]: {
                  minWidth: 'unset !important',
                  padding: '4px 8px',
                  width: '100%',
                },
              }}
              loading={isLoadingBulkUpdate}
              disabled={multiTaskIds?.length === 0}
            >
              <Typography
                variant='body1'
                sx={{
                  cursor: 'pointer',
                  color: themes.color.violet500,
                  fontWeight: 500,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.4,
                }}
              >
                <img src={CompleteIcon} alt='complete-icon' /> Complete Tasks
              </Typography>
            </Button>
            <Divider
              orientation='vertical'
              sx={{
                borderColor: '#717D96 !important',
                [themes.breakpoints.down('md')]: {
                  display: 'none',
                },
              }}
            />
            <Button
              noRounder
              buttonType='text'
              onClick={() => {
                setOpenBulkAssign(true);
              }}
              sx={{
                'minWidth': '76px !important',
                'backgroundColor': 'rgba(234, 234, 248, 1) !important',
                'color': `${themes.color.violet700} !important`,
                'padding': '4px 16px',
                'borderRadius': '8px !important',
                '&.Mui-disabled': {
                  color: ' #C9C9C9 !important',
                  backgroundColor: '#F8F8F8 !important',
                },
                [themes.breakpoints.down('md')]: {
                  minWidth: 'unset !important',
                  padding: '4px 8px',
                  width: '100%',
                },
              }}
              loading={isLoadingExport}
              disabled={multiTaskIds?.length === 0}
            >
              <Typography
                variant='body1'
                sx={{
                  cursor: 'pointer',
                  color: themes.color.violet500,
                  fontWeight: 500,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.4,
                }}
              >
                <img src={DriverIcon} alt='driver-icon' /> Assign Tasks
              </Typography>
            </Button>
          </>
        )}
      </Stack>
    </Stack>
  );

  const renderAssignDriver = () => {
    if (!openBulkAssign) return '';
    return (
      <AssignDriver
        taskId={null}
        hubId=''
        multiTaskIds={multiTaskIds}
        isAssignMultiTask
        setMultiTaskIds={() => {
          setOpenBulkAssign(false);
        }}
        onBackToTaskDetail={() => setOpenBulkAssign(false)}
        handleCloseTaskDetail={() => {
          setTasksSelected([]);
          setOpenBulkAssign(false);
        }}
      />
    );
  };

  return (
    <>
      <Table
        customClassName={clsx({
          ongoing: tabValue === ITabValue.Ongoing,
          completed: tabValue === ITabValue.Completed,
        })}
        containerSxProps={{
          ...(isFullEmpty
            ? {
                '& td': {
                  filter: 'grayscale(1)',
                  opacity: 0.25,
                },
              }
            : {}),
          [themes.breakpoints.down('md')]: {
            '& table': {
              '& .MuiChip-root': {
                width: '100px !important',
              },
            },
          },
        }}
        headerCellSxProps={{
          'color': themes.color.gray400,
          'backgroundColor': themes.color.gray200,
          'fontSize': 12,
          'paddingTop': '4px !important',
          'paddingBottom': '4px !important',
          'border': 'none',
          '& #name-sortable': {
            display: 'flex !important',
          },
        }}
        cellSxProps={{
          paddingTop: '8px !important',
          paddingBottom: '8px !important',
          color: themes.color.violet500,
        }}
        columns={columns}
        dataProps={isFullEmpty ? EmptyStateData(tabValue) : data}
        sortBy={convertedSortParams(sort, sort_by)}
        onSort={handleSort}
        clickableRowId={clickableRowId}
        onRowClick={onRowClick}
        striped={false}
        customEmptyState={renderTableEmptyState}
        loading={isLoading}
        sxNoData={{ padding: '35px !important' }}
      />
      {taskIdsSelected?.length !== 0 && renderTaskSelected()}
      <StyledPanelDetailContainer
        role='presentation'
        isOpen={openBulkAssign}
        fixedWidth={480}
      >
        {renderAssignDriver()}
      </StyledPanelDetailContainer>
    </>
  );
};
